/**
 * ReportingWindowOverride
 *
 * Lets a user opt-in to an instrument-level reporting window that differs from
 * the dashboard-level global window.  The anchor month is always inherited from
 * the dashboard; only the window type can be overridden here.
 *
 * Placement:
 *   - Line / Bar / Stack / Table / Variance → above DashboardModalFilters
 *   - KPI → after ModalIndicator
 */

import { Box, Menu, MenuButton, MenuItem, MenuList } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';

import FigmaTriangleDown from 'components/Icons/FigmaTriangleDown';
import { getReportingAnchorPeriod, getReportingWindow } from 'redux/DashboardSliceV2';
import { getBlocks } from 'redux/ModelsSlice';
import {
  type ReportingWindow,
  REPORTING_WINDOW_LABELS,
  resolveReportingPeriod,
} from 'utils/reportingPeriodUtils';

/** Sentinel value used in the <select> to represent "follow dashboard global". */
const FOLLOW_GLOBAL_VALUE = 'FOLLOW_GLOBAL';

interface ReportingWindowOverrideProperties {
  /** Current instrument formatting object (read). */
  formatting: Record<string, any>;
  /** Called whenever the user changes the override settings. */
  onChange: (updatedFormatting: Record<string, any>) => void;
}

const ReportingWindowOverride = ({ formatting, onChange }: ReportingWindowOverrideProperties) => {
  // Chakra typings can hit TS complexity limits; cast these primitives to `any`.
  const MenuAny = Menu as any;
  const MenuButtonAny = MenuButton as any;
  const MenuListAny = MenuList as any;
  const MenuItemAny = MenuItem as any;
  const globalAnchor = useSelector(getReportingAnchorPeriod);
  const globalWindow = useSelector(getReportingWindow);
  const model = useSelector(getBlocks);
  const fyStartMonth: string =
    (model?.time_properties?.fy_start_month as string | undefined) ?? 'Jan';

  const mode: string = formatting?.reporting_window_mode || 'FOLLOW_GLOBAL';
  const overrideWindow: ReportingWindow = formatting?.reporting_window_override || globalWindow;

  // The select value is either the override window key or the sentinel "follow global"
  const selectValue = mode === 'OVERRIDE' ? overrideWindow : FOLLOW_GLOBAL_VALUE;

  // The active window for period resolution: override if set, otherwise global
  const activeWindow = mode === 'OVERRIDE' ? overrideWindow : globalWindow;

  /** Resolved start/end for the currently effective window. */
  const activePeriod = useMemo(
    () => resolveReportingPeriod(globalAnchor, activeWindow, fyStartMonth),
    [globalAnchor, activeWindow, fyStartMonth],
  );

  const periodHint =
    activePeriod.start !== activePeriod.end
      ? `${activePeriod.start} – ${activePeriod.end}`
      : activePeriod.end;

  const handleMenuSelect = (value: string) => {
    if (value === FOLLOW_GLOBAL_VALUE) {
      // Revert to dashboard global — clear the override
      onChange({
        ...formatting,
        reporting_window_mode: 'FOLLOW_GLOBAL',
        reporting_window_override: undefined,
      });
    } else {
      onChange({
        ...formatting,
        reporting_window_mode: 'OVERRIDE',
        reporting_window_override: value as ReportingWindow,
      });
    }
  };

  // Label shown in the button: "Dashboard default" or the window name
  const selectedLabel =
    selectValue === FOLLOW_GLOBAL_VALUE
      ? 'Auto from dashboard'
      : REPORTING_WINDOW_LABELS[selectValue as ReportingWindow];

  return (
    <Box>
      <Box
        as='span'
        fontFamily='Roboto Bold'
        fontWeight='600'
        fontSize='13px'
        color='#121826'
        mb='4px'
        display='block'
      >
        Time window
      </Box>
      <MenuAny matchWidth isLazy strategy='fixed'>
        <MenuButtonAny
          w='100%'
          px='11px'
          py='11px'
          bg='white'
          borderRadius='9px'
          border='1px solid #D9D9D9'
        >
          <Box display='flex' alignItems='center' justifyContent='space-between'>
            <Box as='span' fontFamily='Roboto Regular' fontSize='13px' color='#121826'>
              {selectedLabel}
            </Box>
            <FigmaTriangleDown width={10} height={6} color='#708090' />
          </Box>
        </MenuButtonAny>
        <MenuListAny
          zIndex={20}
          minW={0}
          p={0}
          bg='#E8E9EB'
          border='none'
          borderRadius='14px'
          maxH='320px'
          overflowY='auto'
          overflowX='hidden'
          className='scroll'
          boxShadow='0px 4px 24px rgba(0, 0, 0, 0.25)'
        >
          {/* First item reverts to dashboard global */}
          <MenuItemAny
            onClick={() => handleMenuSelect(FOLLOW_GLOBAL_VALUE)}
            bg='white'
            mb='1px'
            px='11px'
            py='9px'
            fontFamily='Roboto Regular'
            fontSize='13px'
            color='#121826'
            _hover={{ bg: '#F5F5F5' }}
          >
            Auto from dashboard
          </MenuItemAny>
          {(Object.keys(REPORTING_WINDOW_LABELS) as ReportingWindow[]).map((key) => (
            <MenuItemAny
              key={key}
              onClick={() => handleMenuSelect(key)}
              bg='white'
              mb='1px'
              px='11px'
              py='9px'
              fontFamily='Roboto Regular'
              fontSize='13px'
              color='#121826'
              _hover={{ bg: '#F5F5F5' }}
            >
              {REPORTING_WINDOW_LABELS[key]}
            </MenuItemAny>
          ))}
        </MenuListAny>
      </MenuAny>
      {/* Hint showing the resolved date range for the current selection */}
      {periodHint && (
        <Box
          as='span'
          fontFamily='Roboto Regular'
          fontSize='11px'
          color='#BEBEBE'
          display='block'
          mt='4px'
        >
          {periodHint}
        </Box>
      )}
    </Box>
  );
};

export default ReportingWindowOverride;
